import * as z from 'zod';

/**
 * Wire schema for a SINGLE legacy order (`GET /orders/{id}`), backing the
 * order-detail drawer.
 *
 * Captured off `tback.noor.uz` (order 239081, 2026-08-18), not from a spec.
 * Two shape facts matter and neither is guessable:
 *
 * 1. **The response is UNENVELOPED.** The list endpoint answers
 *    `{data, count, …}`, but this one puts the order's fields at the TOP
 *    level — there is no `data` wrapper to unwrap. The old app's
 *    `orderData?.data?.…` reads work only because its axios wrapper adds a
 *    `data` layer of its own.
 * 2. **The same nested object differs between legs.** A pickup's `client`
 *    carries `{phone, name}`, a drop-off's only `{phone}`. Requiring `name`
 *    fails the WHOLE payload on a perfectly normal order, so it is optional.
 *
 * Everything the drawer does not render is left unmodelled (Zod strips it);
 * everything it does render is defaulted, because this backend omits keys
 * rather than sending nulls and a missing key must not blank the drawer.
 */

/** A geographic point. Both members are required — the map needs both. */
export const legacyPointSchema = z.object({
  lat: z.number(),
  long: z.number(),
});

/**
 * A cargo line item inside a leg's `products`.
 *
 * `weight` is in GRAMS (the manifest divides by 1000 to show kg) and the three
 * dimensions are unitless numbers the manifest concatenates as `HxW xL`.
 */
export const legacyOrderItemSchema = z.object({
  name: z.string().default(''),
  quantity: z.number().default(0),
  price_per_unit: z.number().default(0),
  weight: z.number().nullable().default(null),
  height: z.number().nullable().default(null),
  width: z.number().nullable().default(null),
  length: z.number().nullable().default(null),
  /**
   * Storage-relative photo path. The EDIT screen must carry it back through
   * the full-body PATCH — the old panel preserves it (`edit/index.tsx:472`),
   * and dropping it here deletes the photo on every edit.
   */
  photo: z.string().nullable().default(null),
});

/** The goods attached to one leg. `items` is empty for a plain delivery. */
export const legacyOrderProductsSchema = z.object({
  id: z.union([z.string(), z.number()]).nullable().default(null),
  type_id: z.number().nullable().default(null),
  description: z.string().nullable().default(null),
  items: z.array(legacyOrderItemSchema).nullable().default([]),
});

/**
 * The contact on a leg. `name` is present on pickups and ABSENT on drop-offs
 * — see the header note; this is the field that fails the parse if required.
 */
export const legacyLegClientSchema = z.object({
  phone: z.string().nullable().default(null),
  name: z.string().nullable().default(null),
});

/**
 * One leg — a pickup (`origin[]`) or a drop-off (`destination[]`). Both arrays
 * use this identical shape, which is why the drawer renders them with one
 * component.
 */
export const legacyOrderLegSchema = z.object({
  id: z.string().nullable().default(null),
  location: legacyPointSchema.nullable().default(null),
  /** 1-based position across the whole route, pickups and drop-offs together. */
  order: z.number().default(0),
  address: z.string().nullable().default(null),
  entrance: z.string().nullable().default(null),
  door_phone: z.string().nullable().default(null),
  apartment: z.string().nullable().default(null),
  floor: z.union([z.string(), z.number()]).nullable().default(null),
  comment: z.string().nullable().default(null),
  comment_for_shop: z.string().nullable().default(null),
  /** ETA timestamps — ISO strings, null before the courier is en route. */
  initial_eta: z.string().nullable().default(null),
  eta: z.string().nullable().default(null),
  /** How far from the point the courier actually stopped, in metres. */
  closed_point_at_distance: z.number().nullable().default(null),
  /** Progress markers: null until the courier reaches / clears the leg. */
  arrived_at: z.string().nullable().default(null),
  handed_over_at: z.string().nullable().default(null),
  skipped_at: z.string().nullable().default(null),
  returned_at: z.string().nullable().default(null),
  products: legacyOrderProductsSchema.nullable().default(null),
  client: legacyLegClientSchema.nullable().default(null),
});

/**
 * The price breakdown. Every member is a plain sum in so'm.
 *
 * `dead_head` and `door_to_door` are MULTIPLIERS/COUNTS here, not prices —
 * the formula card multiplies them against their per-unit rates rather than
 * displaying them directly.
 */
export const legacyOrderPricingSchema = z.object({
  total: z.number().default(0),
  products: z.number().default(0),
  order: z.number().default(0),
  dead_head: z.number().default(0),
  additional_fixed_price: z.number().default(0),
  door_to_door: z.number().default(0),
  courier_profit_add_on: z.number().default(0),
  discount: z.number().nullable().default(null),
  need_debt: z.boolean().default(false),
  given_debt: z.boolean().default(false),
  wait_price: z.number().default(0),
  delivery_base_price: z.number().default(0),
  /** Fiscal receipt links, null until the receipt is issued. */
  ofd_check_url: z.string().nullable().default(null),
  product_ofd_check_url: z.string().nullable().default(null),
  base_price_paid: z.boolean().default(false),
});

/** Route distances, in METRES (the drawer divides by 1000 to show km). */
export const legacyOrderDistancesSchema = z.object({
  order: z.number().default(0),
  dead_head: z.number().default(0),
  initial_dead_head_distance: z.number().default(0),
});

/** Delivery terms — scheduling, tariff class, and the paid-goods flag. */
export const legacyOrderDeliveryBlockSchema = z.object({
  type: z.string().nullable().default(null),
  /** Scheduled slot; null for an immediate order. */
  time: z.string().nullable().default(null),
  door_to_door: z.boolean().default(false),
  /** Whether the courier fronts the cost of the goods. */
  product_paid: z.boolean().default(false),
  equipment_id: z.number().nullable().default(null),
  /** Whether a public tracking link was issued for this order. */
  send_link: z.boolean().default(false),
  delivery_base_price: z.number().default(0),
});

/** The assigned courier; null while the order is still searching. */
export const legacyOrderCourierSchema = z.object({
  id: z.number(),
  first_name: z.string().nullable().default(null),
  last_name: z.string().nullable().default(null),
  middle_name: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  profile_photo: z.string().nullable().default(null),
  average_rating: z.number().nullable().default(null),
  location: legacyPointSchema.nullable().default(null),
});

/**
 * The merchant account that placed the order — the "client" card's source.
 * NOT the same thing as `origin[0].client` (the pickup contact person).
 */
export const legacyOrderVendorSchema = z.object({
  name: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
});

/** The full order, as `GET /orders/{id}` returns it (no envelope). */
export const legacyOrderDetailSchema = z.object({
  id: z.number(),
  display_id: z.number(),
  vendor_order_id: z.string().nullable().default(null),
  /** Cancellation reason — populated only on a cancelled order. */
  reason: z.string().nullable().default(null),
  stage: z.number(),
  payment_type: z.string().nullable().default(null),
  origin: z.array(legacyOrderLegSchema).default([]),
  destination: z.array(legacyOrderLegSchema).default([]),
  pricing: legacyOrderPricingSchema.default(() => legacyOrderPricingSchema.parse({})),
  distances: legacyOrderDistancesSchema.default(() =>
    legacyOrderDistancesSchema.parse({}),
  ),
  delivery: legacyOrderDeliveryBlockSchema.default(() =>
    legacyOrderDeliveryBlockSchema.parse({}),
  ),
  courier: legacyOrderCourierSchema.nullable().default(null),
  /** Public tracking link; only meaningful when `delivery.send_link` is set. */
  link: z.string().nullable().default(null),
  created_at: z.string().nullable().default(null),
  accepted_at: z.string().nullable().default(null),
  total_late_minutes: z.number().default(0),
  free_wait_time: z.number().default(0),
  /**
   * Nullable, NOT defaulted to 0: the old panel falls back to 500 so'm/min
   * when the rate is absent or negative, and a `0` default would silently
   * disable that fallback (see `extraWaitPrice`).
   */
  price_per_minute_after_wait: z.number().nullable().default(null),
  is_business: z.boolean().default(false),
  courier_delivery_comment: z.string().nullable().default(null),
  vendor: legacyOrderVendorSchema.nullable().default(null),
  /** Goods total across all legs — `0`/absent means "no products". */
  total_product_price: z.number().default(0),
  /** Courier payment state — ids map through `orderCourierPaymentStatuses`. */
  courier_payment_status: z.number().nullable().default(null),
  courier_paid_at: z.string().nullable().default(null),
  /** Storage-relative path of the courier's payment receipt photo. */
  courier_payment_receipt_url: z.string().nullable().default(null),
});

export type LegacyPoint = z.infer<typeof legacyPointSchema>;
export type LegacyOrderItem = z.infer<typeof legacyOrderItemSchema>;
export type LegacyOrderLeg = z.infer<typeof legacyOrderLegSchema>;
export type LegacyOrderPricing = z.infer<typeof legacyOrderPricingSchema>;
export type LegacyOrderDetail = z.infer<typeof legacyOrderDetailSchema>;
